import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, Check, X, ChevronDown, ChevronUp } from "lucide-react";
import { BLUEPRINT_CHECKOUT_URL, withUTM } from "@/config/blueprint";
import BlueprintLeadModal from "@/components/BlueprintLeadModal";

// -----------------------------------------------------------------------------
// $497 Bounce City University Blueprint — direct-purchase sales page
// -----------------------------------------------------------------------------
// All purchase CTAs route through <BuyButton /> so the destination URL is
// controlled in one place (BLUEPRINT_CHECKOUT_URL) and UTM params are appended
// consistently. Purchase events are prepared for GA4 + Meta Pixel + GHL below
// but do NOT include any fake IDs — they only fire when the runtime globals
// (window.gtag, window.fbq, window.ghlTrack) already exist.
// -----------------------------------------------------------------------------

const PRICE = "$497";
const CHECKOUT_TESTID = "bp-cta-checkout";

// Opens the pre-checkout lead modal. Placement identifies which CTA fired.
function openLeadModal(placement) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "blueprint_checkout_modal_open", { placement });
    }
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("trackCustom", "blueprint_checkout_modal_open", { placement });
    }
  } catch { /* ignore */ }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("bcu:open-blueprint-modal", { detail: { placement } }));
  }
}

function BuyButton({ children, placement, className = "bcu-btn-primary", testid = CHECKOUT_TESTID }) {
  const onClick = (e) => {
    e.preventDefault();
    openLeadModal(placement);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-placement={placement}
      className={className}
    >
      {children}
    </button>
  );
}

// -----------------------------------------------------------------------------
// Static content
// -----------------------------------------------------------------------------

const PROBLEM_QUESTIONS = [
  "What equipment should I buy first?",
  "How much money do I actually need?",
  "How should I price my rentals?",
  "How do I get my first bookings?",
  "What systems do I need?",
  "What should I build first?",
  "How should operations, delivery, cleaning, staffing, and customer service work?",
];

const AUDIENCE = [
  { t: "Starting From Zero", d: "You want to enter the bounce house rental business but don't know how to structure the first steps." },
  { t: "Early-Stage Operator", d: "You may already own a few units, but the business still feels disorganized or inconsistent." },
  { t: "Serious About Building It Correctly", d: "You want to understand the business before wasting money on the wrong equipment, marketing, or systems." },
  { t: "Prefer to Learn at Your Own Pace", d: "You want the Blueprint without paying for private coaching or mentorship." },
];

const BEFORE = [
  "Unsure where to begin",
  "Random information from different sources",
  "Confused about equipment",
  "No clear marketing plan",
  "Unclear pricing",
  "No operational roadmap",
  "Unsure what to build first",
];
const AFTER = [
  "Understand the major steps",
  "Know the order to build the business",
  "Understand equipment strategy",
  "Understand marketing and sales",
  "Understand operations",
  "Understand systems",
  "Have a roadmap for growth",
];

const PHASES = [
  {
    id: "foundation",
    n: "01",
    title: "Foundation",
    headline: "Build the Right Foundation",
    color: "#F7931E",
    description: "Learn the essential pieces you need to understand before launching and operating your bounce house rental business.",
    topics: [
      "Business foundation",
      "Brand positioning",
      "Startup planning",
      "Equipment strategy",
      "Financial preparation",
      "Understanding what needs to be in place before launch",
    ],
    outcome: "By the end of this phase, you should understand the core foundation needed to start building the business correctly.",
  },
  {
    id: "building",
    n: "02",
    title: "Building",
    headline: "Build the Business",
    color: "#168CFF",
    description: "Learn how the different parts of the business work together so you can move from planning into actual operations and customer acquisition.",
    topics: [
      "Day-to-day operations",
      "Business systems",
      "Marketing",
      "Sales",
      "Customer journey",
      "Event execution",
      "Building consistency",
    ],
    outcome: "By the end of this phase, you should understand how to operate, market, sell, and organize the business more effectively.",
  },
  {
    id: "scaling",
    n: "03",
    title: "Scaling",
    headline: "Prepare to Scale",
    color: "#78C800",
    description: "Learn what needs to change as the business grows and becomes more organized, more visible, and less dependent on the owner doing everything.",
    topics: [
      "Content and visibility",
      "Team and systems",
      "Growth planning",
      "Operational improvement",
      "Expansion thinking",
      "Next-stage business structure",
    ],
    outcome: "By the end of this phase, you should understand what needs to be strengthened before scaling the business further.",
  },
];

const INCLUDED_ITEMS = [
  { t: "Full 12-Module Blueprint", d: "Access the complete recorded curriculum." },
  { t: "12 Months of Course Access", d: "Return to the lessons and revisit the training as you build." },
  { t: "BCU Skool Community", d: "Stay inside the Bounce City University community for the full 12-month access period." },
  { t: "Blueprint Resources", d: "Access the templates, worksheets, checklists, and resources already included with the course." },
  { t: "Self-Paced Learning", d: "Move through the training according to your own schedule." },
  { t: "One-Time Payment", d: "No recurring course subscription." },
];

const INCLUDED_LIST = [
  "12 recorded modules",
  "12-month course access",
  "12-month Skool community access",
  "Course resources",
  "Self-paced education",
];
const NOT_INCLUDED_LIST = [
  "Direct access to Emmanuel",
  "Private coaching",
  "1-on-1 strategy",
  "Customized business plans",
  "Implementation support",
  "Private feedback",
  "Live Boot Camp access",
  "Graduation Weekend access",
  "Mentorship-level support",
];

const FAQS = [
  { q: "Is this a live mentorship?", a: "No. The $497 Bounce City University Blueprint is a self-paced recorded education program." },
  { q: "Will I get direct access to Emmanuel?", a: "No. Direct access, personalized strategy, implementation support, and private coaching are reserved for the premium BCU mentorship." },
  { q: "How long do I get access?", a: "You receive 12 months of access to the Blueprint course and the BCU Skool community." },
  { q: "Do I have to complete it in 90 days?", a: "No. The program is self-paced during your 12-month access period." },
  { q: "Is this only for people who already own bounce houses?", a: "No. The Blueprint is designed to help beginners understand the process from the beginning while also being useful for early-stage operators who need more structure." },
  { q: "Are Boot Camp and Graduation Weekend included?", a: "No. Those live experiences are not included with the $497 self-paced Blueprint." },
  { q: "Is this a done-for-you business?", a: "No. The Blueprint teaches the roadmap. You are responsible for applying and implementing the information." },
  { q: "Does buying the course guarantee business results or income?", a: "No. Results depend on many factors including implementation, market, effort, budget, decision-making, and execution." },
  { q: "How much does the course cost?", a: "$497 as a one-time payment." },
  { q: "Will I be charged monthly?", a: "No. The course price is a one-time $497 payment." },
];

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export default function Blueprint() {
  const location = useLocation();
  const finalCtaRef = useRef(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPlacement, setModalPlacement] = useState("");

  // Global listener so Header/Footer CTAs (rendered outside this page tree)
  // can open the modal by dispatching a window event with a placement label.
  useEffect(() => {
    const onOpen = (e) => {
      setModalPlacement((e && e.detail && e.detail.placement) || "unknown");
      setModalOpen(true);
    };
    window.addEventListener("bcu:open-blueprint-modal", onOpen);
    return () => window.removeEventListener("bcu:open-blueprint-modal", onOpen);
  }, []);

  // Route-specific browser title + meta description + Open Graph tags.
  // Set on mount, restored on unmount so every other page keeps its own
  // metadata from index.html. Uses vanilla DOM (no react-helmet dep).
  useEffect(() => {
    const restores = [];
    const prevTitle = document.title;
    document.title = "Bounce City University Blueprint — $497 Self-Paced Program";
    restores.push(() => { document.title = prevTitle; });

    const items = [
      ['meta[name="description"]', "content", "Learn the step-by-step roadmap for starting, marketing, operating, and growing a bounce house rental business with the 12-module Bounce City University Blueprint."],
      ['meta[property="og:title"]', "content", "Bounce City University Blueprint — $497"],
      ['meta[property="og:description"]', "content", "Get the complete step-by-step roadmap for building your bounce house rental business. 12 modules. 12 months access. $497 one-time."],
    ];
    for (const [selector, attr, value] of items) {
      const el = document.querySelector(selector);
      if (!el) continue;
      const prev = el.getAttribute(attr) || "";
      el.setAttribute(attr, value);
      restores.push(() => { el.setAttribute(attr, prev); });
    }
    return () => { restores.forEach((r) => r()); };
  }, []);

  const checkoutIsPlaceholder = BLUEPRINT_CHECKOUT_URL.startsWith("#");

  // Capture UTMs once on load so they're queryable even if the visitor navigates
  // internally before purchasing. Stored in sessionStorage under a stable key.
  useEffect(() => {
    try {
      const q = new URLSearchParams(location.search);
      const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
      const captured = {};
      for (const k of keys) { const v = q.get(k); if (v) captured[k] = v; }
      if (Object.keys(captured).length) {
        sessionStorage.setItem("bcu_utm", JSON.stringify(captured));
      }
    } catch { /* ignore */ }
  }, [location.search]);

  // Show the mobile sticky CTA once the visitor has scrolled past the hero, and
  // hide it again once the pricing/final section is visible so it never blocks
  // the primary purchase card.
  useEffect(() => {
    let pastHero = false;
    let atFinal = false;
    const update = () => setShowStickyCta(pastHero && !atFinal);
    const onScroll = () => {
      pastHero = window.scrollY > 500;
      update();
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.target === finalCtaRef.current) { atFinal = e.isIntersecting; update(); }
      }
    }, { threshold: 0.15 });
    if (finalCtaRef.current) io.observe(finalCtaRef.current);
    return () => { window.removeEventListener("scroll", onScroll); io.disconnect(); };
  }, []);

  return (
    <div data-testid="blueprint-page" className="bg-black text-white">
      {/* ---------------- HERO ---------------- */}
      <section className="sect-black pt-36 md:pt-44 pb-20 md:pb-24">
        <div className="bcu-container">
          <p className="bcu-eyebrow bcu-eyebrow-gold">The Bounce City University Blueprint</p>
          <h1 className="mt-6 font-display text-white text-5xl md:text-7xl lg:text-[86px] leading-[1.02] font-medium max-w-5xl">
            Stop Guessing How to <span className="font-editorial italic text-[#F7931E]">Start Your Bounce House Business</span>.
          </h1>
          <h2 className="mt-6 font-display text-white/90 text-2xl md:text-3xl lg:text-4xl leading-[1.15] font-medium max-w-4xl">
            Follow the Complete Step-by-Step Blueprint.
          </h2>
          <p className="mt-8 text-[#A9A9A9] text-[18px] leading-[1.75] max-w-[640px]">
            Learn the roadmap for setting up, launching, marketing, operating, and growing a bounce house rental business — all inside one self-paced 12-module program.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <span className="inline-flex items-center px-4 py-2 border border-[rgba(247,147,30,0.35)] bg-[rgba(247,147,30,0.08)] text-[#F7931E] text-[12px] tracking-[0.22em] uppercase font-semibold rounded">12 Modules</span>
            <span className="inline-flex items-center px-4 py-2 border border-[rgba(22,140,255,0.35)] bg-[rgba(22,140,255,0.08)] text-[#168CFF] text-[12px] tracking-[0.22em] uppercase font-semibold rounded">12 Months Access</span>
            <span className="inline-flex items-center px-4 py-2 border border-[rgba(120,200,0,0.35)] bg-[rgba(120,200,0,0.08)] text-[#78C800] text-[12px] tracking-[0.22em] uppercase font-semibold rounded">$497 One-Time</span>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <BuyButton placement="hero" testid="bp-cta-hero">Get Instant Access — $497 <ArrowRight className="h-4 w-4" /></BuyButton>
            <a href="#curriculum" data-testid="bp-cta-see-inside" className="bcu-btn-secondary">See What's Inside</a>
          </div>
          <p className="mt-5 text-[13px] text-[#7A7A7A]">Self-paced training. No application or sales call required.</p>
        </div>
      </section>

      {/* ---------------- PROBLEM ---------------- */}
      <section className="sect-black-3 py-20 md:py-24 border-t border-[rgba(255,255,255,0.06)]">
        <div className="bcu-container">
          <p className="bcu-eyebrow bcu-eyebrow-gold">What You'll Learn</p>
          <h2 className="mt-6 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-medium max-w-4xl">
            Starting the Business Shouldn't <span className="font-editorial italic text-[#F7931E]">Feel Like Guesswork</span>.
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-x-10 gap-y-4 max-w-4xl">
            {PROBLEM_QUESTIONS.map((q) => (
              <div key={q} className="flex items-start gap-3 text-[16px] text-[#C7CFDD] leading-relaxed">
                <span className="mt-2.5 h-px w-3 flex-shrink-0 bg-[#F7931E]" />{q}
              </div>
            ))}
          </div>

          <p className="mt-10 text-[#A9A9A9] text-[17px] leading-[1.75] max-w-[720px]">
            Most beginners piece together random information from videos, groups, and different operators — a bit of pricing here, a used inflatable listing there, a Facebook post about permits. It's exhausting and expensive.
          </p>
          <p className="mt-6 font-display text-white text-2xl md:text-3xl leading-[1.2] max-w-3xl">
            You don't need more random information. You need to know <span className="font-editorial italic text-[#F7931E]">what to do next</span>.
          </p>
        </div>
      </section>

      {/* ---------------- WHO THIS IS FOR ---------------- */}
      <section id="who-its-for" className="sect-black py-20 md:py-24 scroll-mt-32">
        <div className="bcu-container">
          <p className="bcu-eyebrow bcu-eyebrow-gold">Built For</p>
          <h2 className="mt-6 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-medium max-w-4xl">
            Built for People Who Want a <span className="font-editorial italic text-[#F7931E]">Clear Starting Point</span>.
          </h2>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {AUDIENCE.map((a, i) => (
              <div key={a.t} className="border border-[rgba(255,255,255,0.08)] bg-[#0D0D0D] p-7 rounded-md">
                <div className="font-display text-3xl text-[#F7931E]">{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-4 font-display text-xl text-white">{a.t}</div>
                <p className="mt-3 text-[15px] text-[#A9A9A9] leading-relaxed">{a.d}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-[14px] text-[#7A7A7A] leading-relaxed max-w-3xl">
            If you want direct strategy and hands-on support from Emmanuel, that is what the separate BCU mentorship is designed for. This $497 Blueprint is the self-paced option.
          </p>
        </div>
      </section>

      {/* ---------------- BEFORE / AFTER ---------------- */}
      <section className="sect-black-3 py-20 md:py-24 border-t border-[rgba(255,255,255,0.06)]">
        <div className="bcu-container">
          <p className="bcu-eyebrow bcu-eyebrow-gold">The Transformation</p>
          <h2 className="mt-6 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-medium max-w-4xl">
            The Goal Is Simple: Replace <span className="font-editorial italic text-[#F7931E]">Confusion With a Roadmap</span>.
          </h2>

          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            <div className="border border-[rgba(255,255,255,0.08)] bg-[#0D0D0D] p-8 rounded-md">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#A9A9A9] font-semibold">Before the Blueprint</div>
              <ul className="mt-6 space-y-3">
                {BEFORE.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px] text-[#A9A9A9]">
                    <X className="h-4 w-4 mt-1 text-[#7A7A7A] flex-shrink-0" />{b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-[#F7931E]/40 bg-[#0D0D0D] p-8 rounded-md shadow-[0_20px_50px_-30px_rgba(247,147,30,0.55)]">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-semibold">After the Blueprint</div>
              <ul className="mt-6 space-y-3">
                {AFTER.map((a) => (
                  <li key={a} className="flex items-start gap-3 text-[15px] text-white">
                    <Check className="h-4 w-4 mt-1 text-[#F7931E] flex-shrink-0" />{a}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12">
            <BuyButton placement="after-transformation" testid="bp-cta-transformation">Get the Blueprint — $497 <ArrowRight className="h-4 w-4" /></BuyButton>
          </div>
        </div>
      </section>

      {/* ---------------- PHASE OVERVIEW ---------------- */}
      <section id="curriculum" className="sect-black py-20 md:py-24 scroll-mt-32">
        <div className="bcu-container">
          <p className="bcu-eyebrow bcu-eyebrow-gold">Foundation · Building · Scaling</p>
          <h2 className="mt-6 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-medium max-w-4xl">
            Three Phases. <span className="font-editorial italic text-[#F7931E]">One Clear Path.</span>
          </h2>
          <p className="mt-6 text-[#A9A9A9] text-[17px] leading-[1.75] max-w-[640px]">
            The complete Blueprint is organized into 12 recorded modules across Foundation, Building, and Scaling. Move through it at your own pace during your 12 months of access.
          </p>

          <div className="mt-14 grid gap-6 lg:gap-8 md:grid-cols-3">
            {PHASES.map((p) => (
              <article
                key={p.id}
                data-testid={`bp-phase-${p.id}`}
                className="relative flex flex-col rounded-md border border-[rgba(255,255,255,0.08)] bg-[#0D0D0D] p-8 md:p-10 overflow-hidden"
                style={{ boxShadow: `0 30px 60px -40px ${p.color}55` }}
              >
                <span aria-hidden className="absolute inset-x-0 top-0 h-[3px]" style={{ background: p.color }} />
                <div className="flex items-baseline gap-4">
                  <div className="font-display text-5xl md:text-6xl font-light leading-none" style={{ color: p.color }}>{p.n}</div>
                  <div className="text-[11px] tracking-[0.24em] uppercase font-semibold" style={{ color: p.color }}>Phase {p.n}</div>
                </div>

                <h3 className="mt-7 font-display text-3xl md:text-4xl text-white leading-[1.1] font-medium">{p.headline}</h3>
                <p className="mt-4 text-[15px] text-[#A9A9A9] leading-relaxed">{p.description}</p>

                <div className="mt-8 pt-6 border-t border-[rgba(255,255,255,0.08)]">
                  <div className="text-[11px] tracking-[0.24em] uppercase text-white/80 font-semibold">What You'll Learn</div>
                  <ul className="mt-5 space-y-3">
                    {p.topics.map((t) => (
                      <li key={t} className="flex items-start gap-3 text-[15px] text-white/90 leading-relaxed">
                        <span className="mt-2.5 h-px w-3 flex-shrink-0" style={{ background: p.color }} />{t}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto pt-8">
                  <div className="pt-6 border-t border-[rgba(255,255,255,0.08)] text-[14px] text-white leading-relaxed">
                    <span className="text-[11px] tracking-[0.24em] uppercase font-semibold" style={{ color: p.color }}>Outcome ·</span> {p.outcome}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA after curriculum */}
      <section className="sect-black py-16 border-t border-[rgba(255,255,255,0.06)]">
        <div className="bcu-container-narrow text-center">
          <BuyButton placement="after-curriculum" testid="bp-cta-after-curriculum">Get the Blueprint — $497 <ArrowRight className="h-4 w-4" /></BuyButton>
        </div>
      </section>

      {/* ---------------- WHAT YOU GET ---------------- */}
      <section id="whats-included" className="sect-black-3 py-20 md:py-24 scroll-mt-32 border-t border-[rgba(255,255,255,0.06)]">
        <div className="bcu-container">
          <p className="bcu-eyebrow bcu-eyebrow-gold">What You Get</p>
          <h2 className="mt-6 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-medium max-w-4xl">
            Everything You Need to <span className="font-editorial italic text-[#F7931E]">Study the Blueprint for the Next 12 Months</span>.
          </h2>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {INCLUDED_ITEMS.map((it, i) => (
              <div key={it.t} className="border border-[rgba(255,255,255,0.08)] bg-[#0D0D0D] p-7 rounded-md">
                <div className="font-display text-2xl text-[#F7931E]">{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-3 font-display text-xl text-white">{it.t}</div>
                <p className="mt-3 text-[15px] text-[#A9A9A9] leading-relaxed">{it.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex items-baseline gap-4">
            <span className="font-display text-5xl md:text-6xl text-[#F7931E]">$497</span>
            <span className="text-[#A9A9A9] text-[15px]">One-Time</span>
          </div>
        </div>
      </section>

      {/* ---------------- INCLUDED / NOT INCLUDED ---------------- */}
      <section className="sect-black py-20 md:py-24 border-t border-[rgba(255,255,255,0.06)]">
        <div className="bcu-container">
          <p className="bcu-eyebrow bcu-eyebrow-gold">Know What You're Buying</p>
          <h2 className="mt-6 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-medium max-w-4xl">
            Know Exactly What <span className="font-editorial italic text-[#F7931E]">You're Buying</span>.
          </h2>
          <p className="mt-6 text-[#A9A9A9] text-[17px] leading-[1.75] max-w-[720px]">
            The $497 Blueprint is intentionally designed as a self-paced education product. It is not the private BCU mentorship.
          </p>

          <div className="mt-12 grid lg:grid-cols-2 gap-6">
            <div className="border border-[#78C800]/40 bg-[#0D0D0D] p-8 rounded-md">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#78C800] font-semibold">Included</div>
              <ul className="mt-6 space-y-3">
                {INCLUDED_LIST.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-[15px] text-white"><Check className="h-4 w-4 mt-1 text-[#78C800] flex-shrink-0" />{it}</li>
                ))}
              </ul>
            </div>
            <div className="border border-[rgba(255,255,255,0.08)] bg-[#0D0D0D] p-8 rounded-md">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#A9A9A9] font-semibold">Not Included</div>
              <ul className="mt-6 space-y-3">
                {NOT_INCLUDED_LIST.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-[15px] text-[#A9A9A9]"><X className="h-4 w-4 mt-1 text-[#7A7A7A] flex-shrink-0" />{it}</li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-10 text-[14px] text-[#7A7A7A] leading-relaxed max-w-3xl">
            The $497 Blueprint gives you the roadmap. The premium mentorship is for people who want direct implementation support from Emmanuel.
          </p>
        </div>
      </section>

      {/* ---------------- WHY BOUNCE CITY ---------------- */}
      <section className="sect-black-3 py-20 md:py-24 border-t border-[rgba(255,255,255,0.06)]">
        <div className="bcu-container">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <p className="bcu-eyebrow bcu-eyebrow-gold">Why Bounce City University</p>
              <h2 className="mt-6 font-display text-white text-4xl md:text-5xl leading-[1.05] font-medium">
                Learn From the <span className="font-editorial italic text-[#F7931E]">Business Behind the Blueprint</span>.
              </h2>
              <div className="mt-6 space-y-5 text-[#A9A9A9] text-[17px] leading-[1.75] max-w-[560px]">
                <p>This program was not created from theory alone. The Blueprint is built around lessons, systems, and experience developed through operating the Bounce City business.</p>
                <p>The training references real events, customers, equipment, staff, marketing, operations, systems, and lessons from real business growth — condensed into a curriculum you can move through at your own pace.</p>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="relative aspect-[4/5] w-full max-w-md mx-auto overflow-hidden rounded-md border border-[rgba(255,255,255,0.08)]">
                <img src="/assets/01_emmanuel_portrait.jpg" alt="Emmanuel Ternier, Founder of Bounce City University" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- PRICE ---------------- */}
      <section id="pricing" className="sect-black py-20 md:py-24 scroll-mt-32 border-t border-[rgba(255,255,255,0.06)]">
        <div className="bcu-container-narrow">
          <p className="bcu-eyebrow bcu-eyebrow-gold justify-center text-center" style={{ display: "flex" }}>Get Access</p>
          <h2 className="mt-6 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-medium text-center">
            Get the Complete Blueprint for <span className="font-editorial italic text-[#F7931E]">$497</span>.
          </h2>

          <div className="mt-12 border border-[#F7931E]/40 bg-[#0D0D0D] p-8 md:p-10 rounded-md shadow-[0_25px_60px_-30px_rgba(247,147,30,0.55)]">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-semibold">Bounce City University Blueprint</div>
            <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {["12 recorded modules","Foundation → Build → Scale curriculum","12 months course access","12 months BCU community access","Blueprint resources","Self-paced learning"].map((x) => (
                <li key={x} className="flex items-start gap-3 text-[15px] text-white"><Check className="h-4 w-4 mt-1 text-[#F7931E] flex-shrink-0" />{x}</li>
              ))}
            </ul>

            <div className="mt-10 pt-8 border-t border-[rgba(255,255,255,0.08)] flex flex-wrap items-end justify-between gap-6">
              <div>
                <div className="font-display text-6xl md:text-7xl text-[#F7931E]">{PRICE}</div>
                <div className="text-[13px] text-[#A9A9A9] mt-2">One-time payment</div>
              </div>
              <BuyButton placement="pricing" testid="bp-cta-pricing">Get Instant Access — $497 <ArrowRight className="h-4 w-4" /></BuyButton>
            </div>

            <p className="mt-6 text-[12px] text-[#7A7A7A]">You'll receive instructions for accessing the program after purchase.</p>
            {checkoutIsPlaceholder && (
              <p data-testid="bp-checkout-placeholder-note" className="mt-2 text-[11px] tracking-[0.14em] uppercase text-[#5A5A5A]">
                Checkout will be connected before launch.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section id="faq" className="sect-black-3 py-20 md:py-24 scroll-mt-32 border-t border-[rgba(255,255,255,0.06)]">
        <div className="bcu-container">
          <p className="bcu-eyebrow bcu-eyebrow-gold">FAQ</p>
          <h2 className="mt-6 font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-medium max-w-4xl">
            Questions <span className="font-editorial italic text-[#F7931E]">answered</span>.
          </h2>

          <div className="mt-12 max-w-4xl">
            {FAQS.map((f, i) => (<FAQItem key={f.q} q={f.q} a={f.a} index={i} />))}
          </div>
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section ref={finalCtaRef} className="sect-black py-24 md:py-28 border-t border-[rgba(217,164,65,0.24)]">
        <div className="bcu-container-narrow text-center">
          <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-medium">
            You Don't Need to <span className="font-editorial italic text-[#F7931E]">Figure Out Every Step Alone</span>.
          </h2>
          <p className="mt-8 text-[#A9A9A9] text-[17px] leading-[1.75] max-w-[640px] mx-auto">
            Get the complete Bounce City University Blueprint and learn the roadmap for building your bounce house rental business from Foundation to Build to Scale.
          </p>
          <div className="mt-8 font-display text-5xl md:text-6xl text-[#F7931E]">$497 <span className="text-[#A9A9A9] text-2xl md:text-3xl">One-Time</span></div>
          <div className="mt-10 flex justify-center">
            <BuyButton placement="final" testid="bp-cta-final">Get Instant Access <ArrowRight className="h-4 w-4" /></BuyButton>
          </div>
          <p className="mt-6 text-[13px] tracking-[0.14em] uppercase text-[#7A7A7A]">12 Modules · 12 Months Access · BCU Community</p>
        </div>
      </section>

      {/* ---------------- STICKY MOBILE CTA ---------------- */}
      <div
        data-testid="bp-sticky-mobile-cta-wrap"
        aria-hidden={!showStickyCta || modalOpen}
        className={`fixed inset-x-0 bottom-0 z-40 lg:hidden transition-transform duration-300 ${showStickyCta && !modalOpen ? "translate-y-0" : "translate-y-full"}`}
      >
        <div className="mx-3 mb-3 rounded-md border border-[#F7931E]/40 bg-black/95 backdrop-blur px-4 py-3 shadow-[0_15px_40px_-15px_rgba(0,0,0,0.9)]">
          <BuyButton placement="sticky_mobile" testid="bp-cta-sticky-mobile" className="bcu-btn-primary w-full justify-center">
            Get the Blueprint — $497 <ArrowRight className="h-4 w-4" />
          </BuyButton>
        </div>
      </div>

      <BlueprintLeadModal open={modalOpen} placement={modalPlacement} onClose={() => setModalOpen(false)} />
    </div>
  );
}

function FAQItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[rgba(255,255,255,0.08)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`bp-faq-${index}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-5 text-left text-white hover:text-[#F7931E] transition-colors"
      >
        <span className="font-display text-lg md:text-xl">{q}</span>
        {open ? <ChevronUp className="h-5 w-5 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 flex-shrink-0" />}
      </button>
      {open && (<div className="pb-6 pr-8 text-[15px] text-[#A9A9A9] leading-relaxed">{a}</div>)}
    </div>
  );
}
